import React, { useState, useEffect, useMemo } from 'react';
import { billsAPI, paymentsAPI, paymentAccountAPI, householdAPI, billCategoriesAPI, incomeAPI } from '../services/api';
import { formatAmount } from '../utils/formatters';
import { parseDate, toDateString, formatDate, formatDateRelative } from '../utils/dateUtils';
import { useToast } from '../context/ToastContext';
import { ConfirmModal, BulkImportModal, ManageIncomeModal, ViewIncomeModal } from '../components/modals';
import BillWizard from '../components/BillWizard';
import BillSearchInput from '../components/common/BillSearchInput';
import DatePicker from '../components/DatePicker';
import BaseModal from '../components/common/BaseModal';
import AccountSelect from '../components/common/AccountSelect';
import CurrencyInput from '../components/common/CurrencyInput';
import TextInput from '../components/common/TextInput';
import BillIcon from '../components/common/BillIcon';
import BentoCard from '../components/common/BentoCard';
import OwnershipBadge from '../components/common/OwnershipBadge';
import { CalendarView, AgendaView } from '../components/views';
import {
  PlusCircle, Edit, Trash2, Calendar, CheckCircle, CreditCard, Zap,
  AlertCircle, ExternalLink, Eye, EyeOff, LayoutGrid, List,
  ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Upload,
  DollarSign, Wallet
} from 'lucide-react';
import '../styles/Page.css';
import '../styles/BillManagerV2.css';
import '../styles/Calendar.css';

// ============================================================================
// VIEW BILL MODAL
// ============================================================================
function ViewBillModal({ isOpen, onClose, bill, onEdit, onMarkPaid, onToggleActive, onDelete, paymentAccounts, members, isInHousehold, payment, onEditPayment }) {
  if (!bill) return null;

  const dueDate = bill.dueInfo?.dueDate;
  const isPaid = bill.dueInfo?.isPaid;
  const isOverdue = bill.dueInfo?.isOverdue;
  const dueDateFormatted = formatDate(dueDate);
  const isFromPayment = !!payment;
  const paymentDateFormatted = payment ? formatDate(payment.payment_date) : null;
  const paymentAccount = paymentAccounts?.find(a => a.id === bill.payment_account_id);
  const assignee = members?.find(m => m.id === bill.assigned_to);

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} title="Bill Details" size="md">
      <div className="view-bill-modal">
        <div className={`view-bill-card ${isOverdue ? 'overdue' : ''} ${isPaid ? 'paid' : ''} ${!bill.is_active ? 'inactive' : ''}`}>
          {bill.icon_url && (
            <div className="view-bill-card-icon">
              <img src={bill.icon_url} alt="" />
            </div>
          )}
          <div className="view-bill-card-info">
            <h2>{bill.bill_name}</h2>
            <div className="view-bill-card-meta">
              <span>{bill.category || 'Uncategorized'}</span>
              <span>•</span>
              <span>{bill.billing_frequency}</span>
            </div>
            {bill.auto_pay && <div className="autopay-badge"><Zap size={12} /> Auto-Pay</div>}
          </div>
          <div className="view-bill-card-amount">
            <div className="amount">{formatAmount(bill.amount)}</div>
            {isOverdue && <div className="status overdue"><AlertCircle size={14} /> Overdue</div>}
            {isPaid && <div className="status paid"><CheckCircle size={14} /> Paid</div>}
            {!bill.is_active && <div className="status inactive"><EyeOff size={14} /> Inactive</div>}
          </div>
        </div>

        <div className="view-bill-details">
          <div className="view-bill-detail-row">
            <span className="detail-label">{isFromPayment ? 'Payment Date' : 'Next Due Date'}</span>
            <span className={`detail-value ${!isFromPayment && isOverdue ? 'overdue' : ''}`}>
              <Calendar size={14} />
              {isFromPayment ? paymentDateFormatted : (dueDateFormatted || 'Not set')}
            </span>
          </div>

          {paymentAccount && (
            <div className="view-bill-detail-row">
              <span className="detail-label">Payment Account</span>
              <span className="detail-value detail-value-with-image">
                {paymentAccount.card_image && <img src={paymentAccount.card_image} alt="" className="card-image-preview" />}
                {paymentAccount.account_name}
              </span>
            </div>
          )}

          {isInHousehold && (
            <>
              <div className="view-bill-detail-row">
                <span className="detail-label">Ownership</span>
                <span className="detail-value">
                  <OwnershipBadge isHousehold={!!bill.household_id} variant="icon-only" size="sm" />
                  {bill.household_id ? 'Household Bill' : 'Personal Bill'}
                </span>
              </div>
              <div className="view-bill-detail-row">
                <span className="detail-label">Assigned To</span>
                <span className="detail-value">
                  {assignee ? `${assignee.first_name} ${assignee.last_name}` : 'Unassigned'}
                </span>
              </div>
            </>
          )}

          {bill.url && (
            <div className="view-bill-detail-row">
              <span className="detail-label">Website</span>
              <a href={bill.url} target="_blank" rel="noopener noreferrer" className="detail-link">
                Pay Online <ExternalLink size={12} />
              </a>
            </div>
          )}
        </div>

        {bill.notes && (
          <div className="view-bill-notes">
            <span className="detail-label">Notes</span>
            <p>{bill.notes}</p>
          </div>
        )}

        <div className="view-bill-actions">
          <div className="view-bill-actions-left">
            <button type="button" className="btn-icon btn-icon-danger" onClick={() => onDelete(bill, true)} title="Delete">
              <Trash2 size={18} />
            </button>
            <button type="button" className="btn-icon" onClick={() => { onClose(); onToggleActive(bill); }} title={bill.is_active ? 'Deactivate' : 'Activate'}>
              {bill.is_active ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          <div className="view-bill-actions-right">
            {!isPaid && bill.is_active && !isFromPayment && (
              <button type="button" className="btn btn-success-soft" onClick={() => onMarkPaid(bill, true)}>
                <CheckCircle size={16} /> Record Payment
              </button>
            )}
            {isFromPayment && onEditPayment && (
              <button type="button" className="btn btn-success-soft" onClick={() => onEditPayment(payment)}>
                <Edit size={16} /> Edit Payment
              </button>
            )}
            <button type="button" className="btn btn-primary-soft" onClick={() => onEdit(bill, true)}>
              <Edit size={16} /> Edit
            </button>
          </div>
        </div>
      </div>
    </BaseModal>
  );
}

// ============================================================================
// MARK AS PAID MODAL
// ============================================================================
function MarkAsPaidModal({ isOpen, onClose, bill, paymentAccounts, onSubmit, saving }) {
  const [formData, setFormData] = useState({
    paymentDate: new Date().toISOString().split('T')[0],
    amount: '',
    paymentAccountId: '',
    confirmationNumber: '',
    notes: ''
  });

  useEffect(() => {
    if (bill) {
      setFormData({
        paymentDate: bill.next_due_date || new Date().toISOString().split('T')[0],
        amount: bill.amount?.toString() || '',
        paymentAccountId: bill.payment_account_id?.toString() || '',
        confirmationNumber: '',
        notes: ''
      });
    }
  }, [bill]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      billId: bill.id,
      paymentDate: formData.paymentDate,
      amount: parseFloat(formData.amount),
      paymentAccountId: formData.paymentAccountId ? parseInt(formData.paymentAccountId) : null,
      confirmationNumber: formData.confirmationNumber || null,
      notes: formData.notes || null
    });
  };

  if (!bill) return null;

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} title="Record Payment" icon={<CheckCircle size={24} />}>
      <div className="modal-body">
        <form id="mark-paid-form" onSubmit={handleSubmit}>
          <div className="payment-bill-summary">
            <div className="bill-info">
              <BillIcon iconUrl={bill.icon_url} categoryIcon="💳" billName={bill.bill_name} size="md" />
              <div>
                <h4>{bill.bill_name}</h4>
                {bill.next_due_date && <p className="text-secondary">Due: {formatDate(bill.next_due_date)}</p>}
              </div>
            </div>
            <div className="bill-amount-display">{formatAmount(bill.amount)}</div>
          </div>

          <div className="form-row-2">
            <div className="form-group">
              <label>Payment Date</label>
              <DatePicker value={formData.paymentDate} onChange={(e) => setFormData({ ...formData, paymentDate: e.target.value })} disabled={saving} />
            </div>
            <div className="form-group">
              <label>Amount Paid</label>
              <CurrencyInput value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} disabled={saving} required />
            </div>
          </div>

          <div className="form-group">
            <label>Payment Account</label>
            <AccountSelect accounts={paymentAccounts} value={formData.paymentAccountId} onChange={(e) => setFormData({ ...formData, paymentAccountId: e.target.value })} disabled={saving} placeholder="Select account..." />
          </div>

          <div className="form-group">
            <label>Confirmation # <span className="optional-label">(Optional)</span></label>
            <TextInput value={formData.confirmationNumber} onChange={(e) => setFormData({ ...formData, confirmationNumber: e.target.value })} placeholder="e.g. TXN123456" disabled={saving} />
          </div>
        </form>
      </div>
      <div className="modal-footer">
        <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
        <button type="submit" form="mark-paid-form" className="btn btn-primary" disabled={saving}>
          {saving ? 'Recording...' : 'Record Payment'}
        </button>
      </div>
    </BaseModal>
  );
}

// ============================================================================
// EDIT PAYMENT MODAL
// ============================================================================
function EditPaymentModal({ isOpen, onClose, payment, bill, paymentAccounts, onSubmit, onDelete, saving }) {
  const [formData, setFormData] = useState({ paymentDate: '', amount: '', paymentAccountId: '', confirmationNumber: '', notes: '' });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (payment) {
      setFormData({
        paymentDate: payment.payment_date || '',
        amount: payment.amount?.toString() || '',
        paymentAccountId: payment.payment_account_id?.toString() || '',
        confirmationNumber: payment.confirmation_number || '',
        notes: payment.notes || ''
      });
    }
  }, [payment]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(payment.id, {
      paymentDate: formData.paymentDate,
      amount: parseFloat(formData.amount),
      paymentAccountId: formData.paymentAccountId ? parseInt(formData.paymentAccountId) : null,
      confirmationNumber: formData.confirmationNumber || null,
      notes: formData.notes || null
    });
  };

  if (!payment) return null;

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} title="Edit Payment" icon={<Edit size={24} />}>
      <div className="modal-body">
        {showDeleteConfirm ? (
          <div className="delete-confirm-content">
            <p>Are you sure you want to delete this payment record?</p>
            <p className="text-secondary">This will mark the bill as unpaid for this period.</p>
            <div className="delete-confirm-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
              <button type="button" className="btn btn-danger" onClick={() => { onDelete(payment.id); setShowDeleteConfirm(false); }} disabled={saving}>
                {saving ? 'Deleting...' : 'Delete Payment'}
              </button>
            </div>
          </div>
        ) : (
          <form id="edit-payment-form" onSubmit={handleSubmit}>
            {bill && (
              <div className="payment-bill-summary">
                <div className="bill-info">
                  <BillIcon iconUrl={bill.icon_url} categoryIcon="💳" billName={bill.bill_name} size="md" />
                  <div>
                    <h4>{bill.bill_name}</h4>
                    <p className="text-secondary">Payment recorded {formatDate(payment.payment_date)}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="form-row-2">
              <div className="form-group">
                <label>Payment Date</label>
                <DatePicker value={formData.paymentDate} onChange={(e) => setFormData({ ...formData, paymentDate: e.target.value })} disabled={saving} />
              </div>
              <div className="form-group">
                <label>Amount Paid</label>
                <CurrencyInput value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} disabled={saving} required />
              </div>
            </div>

            <div className="form-group">
              <label>Payment Account</label>
              <AccountSelect accounts={paymentAccounts} value={formData.paymentAccountId} onChange={(e) => setFormData({ ...formData, paymentAccountId: e.target.value })} disabled={saving} placeholder="Select account..." />
            </div>

            <div className="form-group">
              <label>Confirmation # <span className="optional-label">(Optional)</span></label>
              <TextInput value={formData.confirmationNumber} onChange={(e) => setFormData({ ...formData, confirmationNumber: e.target.value })} placeholder="e.g. TXN123456" disabled={saving} />
            </div>
          </form>
        )}
      </div>
      {!showDeleteConfirm && (
        <div className="modal-footer modal-footer-split">
          <button type="button" className="btn btn-danger-soft" onClick={() => setShowDeleteConfirm(true)} disabled={saving}>
            <Trash2 size={16} /> Delete
          </button>
          <div className="modal-footer-right">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" form="edit-payment-form" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}
    </BaseModal>
  );
}

// ============================================================================
// UTILITY: Calculate next due date for bills outside the 90-day window
// ============================================================================
function calculateNextDueDate(bill) {
  if (!bill.start_date || !bill.billing_frequency) return null;
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const startDate = parseDate(bill.start_date);
  if (!startDate) return null;
  
  const endDate = parseDate(bill.end_date);
  const frequency = bill.billing_frequency;
  
  if (startDate > today) return toDateString(startDate);
  if (endDate && today > endDate) return null;
  
  let nextDue = null;
  
  switch (frequency) {
    case 'one-time':
      nextDue = startDate;
      break;
    case 'weekly': {
      const daysDiff = Math.floor((today - startDate) / (24 * 60 * 60 * 1000));
      const weeksPassed = Math.floor(daysDiff / 7);
      nextDue = new Date(startDate);
      nextDue.setDate(nextDue.getDate() + (weeksPassed * 7));
      if (nextDue < today) nextDue.setDate(nextDue.getDate() + 7);
      break;
    }
    case 'biweekly': {
      const daysDiff = Math.floor((today - startDate) / (24 * 60 * 60 * 1000));
      const biweeksPassed = Math.floor(daysDiff / 14);
      nextDue = new Date(startDate);
      nextDue.setDate(nextDue.getDate() + (biweeksPassed * 14));
      if (nextDue < today) nextDue.setDate(nextDue.getDate() + 14);
      break;
    }
    case 'monthly': {
      const dayOfMonth = startDate.getDate();
      nextDue = new Date(today.getFullYear(), today.getMonth(), 1);
      const daysInMonth = new Date(nextDue.getFullYear(), nextDue.getMonth() + 1, 0).getDate();
      nextDue.setDate(Math.min(dayOfMonth, daysInMonth));
      if (nextDue < today) {
        nextDue.setMonth(nextDue.getMonth() + 1);
        const nextDaysInMonth = new Date(nextDue.getFullYear(), nextDue.getMonth() + 1, 0).getDate();
        nextDue.setDate(Math.min(dayOfMonth, nextDaysInMonth));
      }
      break;
    }
    case 'quarterly': {
      const dayOfMonth = startDate.getDate();
      const startMonth = startDate.getMonth();
      const monthsDiff = (today.getFullYear() - startDate.getFullYear()) * 12 + (today.getMonth() - startDate.getMonth());
      const quartersPassed = Math.floor(monthsDiff / 3);
      nextDue = new Date(startDate.getFullYear(), startMonth + (quartersPassed * 3), 1);
      const daysInMonth = new Date(nextDue.getFullYear(), nextDue.getMonth() + 1, 0).getDate();
      nextDue.setDate(Math.min(dayOfMonth, daysInMonth));
      if (nextDue < today) {
        nextDue.setMonth(nextDue.getMonth() + 3);
        const nextDaysInMonth = new Date(nextDue.getFullYear(), nextDue.getMonth() + 1, 0).getDate();
        nextDue.setDate(Math.min(dayOfMonth, nextDaysInMonth));
      }
      break;
    }
    case 'annually': {
      const billMonth = startDate.getMonth();
      const billDay = startDate.getDate();
      nextDue = new Date(today.getFullYear(), billMonth, 1);
      const daysInMonth = new Date(nextDue.getFullYear(), billMonth + 1, 0).getDate();
      nextDue.setDate(Math.min(billDay, daysInMonth));
      if (nextDue < today) {
        nextDue = new Date(today.getFullYear() + 1, billMonth, 1);
        const nextDaysInMonth = new Date(nextDue.getFullYear(), billMonth + 1, 0).getDate();
        nextDue.setDate(Math.min(billDay, nextDaysInMonth));
      }
      break;
    }
    default: {
      const customMatch = frequency?.match(/^(\d+)-(weekly|monthly|yearly)$/);
      if (customMatch) {
        const interval = parseInt(customMatch[1]);
        const unit = customMatch[2];
        if (unit === 'weekly') {
          const daysDiff = Math.floor((today - startDate) / (24 * 60 * 60 * 1000));
          const cyclesPassed = Math.floor(daysDiff / (7 * interval));
          nextDue = new Date(startDate);
          nextDue.setDate(nextDue.getDate() + (cyclesPassed * 7 * interval));
          if (nextDue < today) nextDue.setDate(nextDue.getDate() + (7 * interval));
        } else if (unit === 'monthly') {
          const dayOfMonth = startDate.getDate();
          const monthsDiff = (today.getFullYear() - startDate.getFullYear()) * 12 + (today.getMonth() - startDate.getMonth());
          const cyclesPassed = Math.floor(monthsDiff / interval);
          nextDue = new Date(startDate.getFullYear(), startDate.getMonth() + (cyclesPassed * interval), 1);
          const daysInMonth = new Date(nextDue.getFullYear(), nextDue.getMonth() + 1, 0).getDate();
          nextDue.setDate(Math.min(dayOfMonth, daysInMonth));
          if (nextDue < today) {
            nextDue.setMonth(nextDue.getMonth() + interval);
            const nextDaysInMonth = new Date(nextDue.getFullYear(), nextDue.getMonth() + 1, 0).getDate();
            nextDue.setDate(Math.min(dayOfMonth, nextDaysInMonth));
          }
        } else if (unit === 'yearly') {
          const billMonth = startDate.getMonth();
          const billDay = startDate.getDate();
          const yearsDiff = today.getFullYear() - startDate.getFullYear();
          const cyclesPassed = Math.floor(yearsDiff / interval);
          nextDue = new Date(startDate.getFullYear() + (cyclesPassed * interval), billMonth, 1);
          const daysInMonth = new Date(nextDue.getFullYear(), billMonth + 1, 0).getDate();
          nextDue.setDate(Math.min(billDay, daysInMonth));
          if (nextDue < today) {
            nextDue = new Date(nextDue.getFullYear() + interval, billMonth, 1);
            const nextDaysInMonth = new Date(nextDue.getFullYear(), billMonth + 1, 0).getDate();
            nextDue.setDate(Math.min(billDay, nextDaysInMonth));
          }
        }
      }
      break;
    }
  }
  
  if (nextDue && endDate && nextDue > endDate) return null;
  return toDateString(nextDue);
}

// ============================================================================
// MAIN COMPONENT: BillManager
// ============================================================================
function BillManager() {
  const { success, error: showError } = useToast();
  
  // ---- Data State ----
  const [bills, setBills] = useState([]);
  const [upcomingBills, setUpcomingBills] = useState([]);
  const [recentPayments, setRecentPayments] = useState([]);
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  const [members, setMembers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [incomeData, setIncomeData] = useState({ personal: [], household: null });
  const [loading, setLoading] = useState(true);

  // ---- UI State ----
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('manageViewMode') || 'bento');
  const [showInactive, setShowInactive] = useState(false);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [expandedSections, setExpandedSections] = useState({});

  // ---- Modal State ----
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showMarkPaidModal, setShowMarkPaidModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditPaymentModal, setShowEditPaymentModal] = useState(false);
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [showIncomeModal, setShowIncomeModal] = useState(false);
  const [showViewIncomeModal, setShowViewIncomeModal] = useState(false);
  
  const [selectedBill, setSelectedBill] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [selectedIncome, setSelectedIncome] = useState(null);
  const [selectedIncomeId, setSelectedIncomeId] = useState(null);
  const [viewedFromPayment, setViewedFromPayment] = useState(null);
  const [returnToViewModal, setReturnToViewModal] = useState(false);
  const [returnToViewIncomeModal, setReturnToViewIncomeModal] = useState(false);
  const [saving, setSaving] = useState(false);

  // ---- User Info ----
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isInHousehold = !!currentUser.household_id;

  // ============================================================================
  // DATA LOADING
  // ============================================================================
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const reloadBills = async () => {
      try {
        const billsResponse = await billsAPI.getAll(showInactive);
        setBills(billsResponse.data);
      } catch (error) {
        console.error('Failed to reload bills:', error);
      }
    };
    if (bills.length > 0 || !loading) reloadBills();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showInactive]);

  const loadData = async (isRefresh = false) => {
    if (!isRefresh) setLoading(true);
    try {
      const [billsRes, upcomingRes, paymentsRes, accountsRes, membersRes, categoriesRes, incomeRes, householdIncomeRes] = await Promise.all([
        billsAPI.getAll(showInactive),
        paymentsAPI.getUpcoming(90),
        paymentsAPI.getAll({ limit: 50 }),
        paymentAccountAPI.getAll(false),
        isInHousehold ? householdAPI.getMembers() : Promise.resolve({ data: [] }),
        billCategoriesAPI.getAll(false),
        incomeAPI.getAll(isInHousehold),
        isInHousehold ? incomeAPI.getHouseholdSummary() : Promise.resolve({ data: { total_monthly_income: 0, members: [] } })
      ]);

      setBills(billsRes.data);
      setUpcomingBills(upcomingRes.data);
      setRecentPayments(paymentsRes.data.payments || []);
      setPaymentAccounts(accountsRes.data);
      setMembers(membersRes.data);
      setCategories(categoriesRes.data);
      setIncomeData({ personal: incomeRes.data || [], household: householdIncomeRes.data || null });
    } catch (error) {
      console.error('Failed to load data:', error);
      showError('Failed to load bill data');
    } finally {
      setLoading(false);
    }
  };

  // ============================================================================
  // COMPUTED DATA
  // ============================================================================
  const getCategoryInfo = (categoryName) => {
    const category = categories.find(c => c.name === categoryName);
    return category ? { icon: category.icon, name: category.name } : { icon: '📋', name: categoryName };
  };

  const getNextDueInfo = (bill) => {
    const upcoming = upcomingBills.find(u => u.id === bill.id);
    if (upcoming) {
      return {
        dueDate: upcoming.due_date,
        isPaid: upcoming.is_paid,
        isOverdue: upcoming.is_overdue,
        daysUntil: upcoming.days_until_due
      };
    }
    const calculatedDueDate = calculateNextDueDate(bill);
    if (calculatedDueDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const dueDate = new Date(calculatedDueDate + 'T00:00:00');
      const daysUntil = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
      return { dueDate: calculatedDueDate, isPaid: false, isOverdue: daysUntil < 0, daysUntil };
    }
    return { dueDate: null, isPaid: false, isOverdue: false, daysUntil: null };
  };

  // Process all bills with due info
  const processedBills = useMemo(() => {
    return bills.map(bill => ({ ...bill, dueInfo: getNextDueInfo(bill) }))
      .sort((a, b) => {
        if (a.is_active !== b.is_active) return a.is_active ? -1 : 1;
        if (!a.dueInfo.dueDate && b.dueInfo.dueDate) return 1;
        if (a.dueInfo.dueDate && !b.dueInfo.dueDate) return -1;
        if (!a.dueInfo.dueDate && !b.dueInfo.dueDate) return a.bill_name.localeCompare(b.bill_name);
        return a.dueInfo.dueDate.localeCompare(b.dueInfo.dueDate);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bills, upcomingBills]);

  // Filter recent payments to last 14 days
  const recentPaymentsFiltered = useMemo(() => {
    const fourteenDaysAgo = new Date();
    fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14);
    fourteenDaysAgo.setHours(0, 0, 0, 0);
    return recentPayments.filter(p => new Date(p.payment_date) >= fourteenDaysAgo);
  }, [recentPayments]);

  // ============================================================================
  // MONTH-CENTRIC DATA (core of v2 design)
  // ============================================================================
  const monthlyData = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const year = calendarDate.getFullYear();
    const month = calendarDate.getMonth();
    const monthStart = new Date(year, month, 1);
    const monthEnd = new Date(year, month + 1, 0, 23, 59, 59, 999);
    
    // Check if bill is paid for a specific due date (payment matching)
    const isBillPaidForDate = (billId, dueDate) => {
      const target = parseDate(dueDate)?.getTime();
      if (!target) return false;
      const earlyWindow = 14 * 24 * 60 * 60 * 1000;
      const lateWindow = 7 * 24 * 60 * 60 * 1000;
      return recentPayments.some(p => {
        if (p.bill_id !== billId) return false;
        const pt = parseDate(p.payment_date)?.getTime();
        return pt && pt >= target - earlyWindow && pt <= target + lateWindow;
      });
    };
    
    // Overdue bills - from ANY month (global)
    const overdueBills = processedBills.filter(b => {
      if (!b.is_active || !b.dueInfo?.dueDate) return false;
      const dueDate = new Date(b.dueInfo.dueDate + 'T00:00:00');
      return dueDate < today && !b.dueInfo.isPaid;
    });
    
    // Bills for selected month (excluding overdue - they show in banner)
    const monthBills = processedBills.filter(b => {
      if (!b.is_active || !b.dueInfo?.dueDate) return false;
      const dueDate = new Date(b.dueInfo.dueDate + 'T00:00:00');
      return dueDate >= monthStart && dueDate <= monthEnd;
    }).map(b => {
      const dueDate = new Date(b.dueInfo.dueDate + 'T00:00:00');
      const isPaid = isBillPaidForDate(b.id, b.dueInfo.dueDate);
      const isOverdue = !isPaid && dueDate < today;
      return { ...b, dueInfo: { ...b.dueInfo, isPaid, isOverdue } };
    }).sort((a, b) => new Date(a.dueInfo.dueDate) - new Date(b.dueInfo.dueDate));
    
    // Split into paid/unpaid and exclude overdue from main list
    const nonOverdueBills = monthBills.filter(b => !b.dueInfo.isOverdue);
    const paidBills = nonOverdueBills.filter(b => b.dueInfo.isPaid);
    const unpaidBills = nonOverdueBills.filter(b => !b.dueInfo.isPaid);
    
    // Stats
    const totalDue = monthBills.reduce((sum, b) => sum + parseFloat(b.amount || 0), 0);
    const totalPaid = paidBills.reduce((sum, b) => sum + parseFloat(b.amount || 0), 0);
    const totalOverdue = overdueBills.reduce((sum, b) => sum + parseFloat(b.amount || 0), 0);
    
    // Next month preview
    const nextMonthStart = new Date(year, month + 1, 1);
    const nextMonthEnd = new Date(year, month + 2, 0, 23, 59, 59, 999);
    const nextMonthBills = processedBills.filter(b => {
      if (!b.is_active || !b.dueInfo?.dueDate) return false;
      const dueDate = new Date(b.dueInfo.dueDate + 'T00:00:00');
      return dueDate >= nextMonthStart && dueDate <= nextMonthEnd;
    }).sort((a, b) => new Date(a.dueInfo.dueDate) - new Date(b.dueInfo.dueDate));
    
    // No due date bills
    const noDueDateBills = processedBills.filter(b => b.is_active && !b.dueInfo.dueDate);
    
    // Inactive bills
    const inactiveBills = processedBills.filter(b => !b.is_active);
    
    return {
      monthBills: nonOverdueBills,
      allMonthBills: monthBills,
      paidBills,
      unpaidBills,
      overdueBills,
      noDueDateBills,
      inactiveBills,
      stats: {
        totalDue,
        totalPaid,
        remaining: totalDue - totalPaid,
        overdueCount: overdueBills.length,
        overdueAmount: totalOverdue
      },
      nextMonth: {
        count: nextMonthBills.length,
        total: nextMonthBills.reduce((sum, b) => sum + parseFloat(b.amount || 0), 0),
        preview: nextMonthBills.slice(0, 10)
      }
    };
  }, [processedBills, recentPayments, calendarDate]);

  // ============================================================================
  // INCOME CALCULATIONS
  // ============================================================================
  const incomeCalcs = useMemo(() => {
    const calculateMonthlyAmount = (amount, frequency) => {
      const numAmount = parseFloat(amount) || 0;
      switch (frequency) {
        case 'weekly': return numAmount * 52 / 12;
        case 'biweekly': return numAmount * 26 / 12;
        case 'semimonthly': return numAmount * 2;
        case 'monthly': return numAmount;
        default: return numAmount;
      }
    };

    const personalMonthly = incomeData.personal
      .filter(i => i.is_active)
      .reduce((sum, inc) => sum + calculateMonthlyAmount(inc.amount, inc.pay_frequency), 0);
    const householdMonthly = incomeData.household?.total_monthly_income || 0;
    const totalMonthlyIncome = isInHousehold && householdMonthly > 0 ? householdMonthly : personalMonthly;

    const totalMonthlyBills = processedBills
      .filter(b => b.is_active)
      .reduce((sum, bill) => {
        const amount = parseFloat(bill.amount) || 0;
        switch (bill.billing_frequency) {
          case 'weekly': return sum + (amount * 52 / 12);
          case 'biweekly': return sum + (amount * 26 / 12);
          case 'monthly': return sum + amount;
          case 'quarterly': return sum + (amount / 3);
          case 'semiannually': return sum + (amount / 6);
          case 'annually': return sum + (amount / 12);
          default: return sum + amount;
        }
      }, 0);

    const billsPercentage = totalMonthlyIncome > 0 ? Math.round((totalMonthlyBills / totalMonthlyIncome) * 100) : 0;

    const getPaydaysInRange = (startDate, endDate) => {
      const paydays = [];
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);

      const activeIncome = incomeData.personal.filter(i => {
        if (!i.is_active) return false;
        return i.user_id === currentUser.id || i.share_with_household;
      });

      activeIncome.forEach(income => {
        const payDateStr = income.next_pay_date?.split('T')[0];
        if (!payDateStr) return;
        
        let nextPayDate = new Date(payDateStr + 'T12:00:00');
        const getIntervalDays = (freq) => {
          switch (freq) {
            case 'weekly': return 7;
            case 'biweekly': return 14;
            case 'semimonthly': return 15;
            case 'monthly': return 30;
            default: return 30;
          }
        };
        const intervalDays = getIntervalDays(income.pay_frequency);
        
        while (nextPayDate > start) {
          if (income.pay_frequency === 'monthly') nextPayDate.setMonth(nextPayDate.getMonth() - 1);
          else nextPayDate.setDate(nextPayDate.getDate() - intervalDays);
        }

        while (nextPayDate <= end) {
          if (nextPayDate >= start) {
            const year = nextPayDate.getFullYear();
            const month = String(nextPayDate.getMonth() + 1).padStart(2, '0');
            const day = String(nextPayDate.getDate()).padStart(2, '0');
            paydays.push({
              date: `${year}-${month}-${day}`,
              income_name: income.income_name,
              amount: parseFloat(income.amount),
              frequency: income.pay_frequency,
              id: income.id,
              icon_url: income.icon_url
            });
          }
          if (income.pay_frequency === 'monthly') nextPayDate.setMonth(nextPayDate.getMonth() + 1);
          else nextPayDate.setDate(nextPayDate.getDate() + intervalDays);
        }
      });

      return paydays.sort((a, b) => a.date.localeCompare(b.date));
    };

    return {
      personalMonthly,
      householdMonthly,
      totalMonthlyIncome,
      totalMonthlyBills,
      billsPercentage,
      hasIncome: incomeData.personal.length > 0 || householdMonthly > 0,
      getPaydaysInRange
    };
  }, [incomeData, processedBills, isInHousehold, currentUser.id]);

  // ============================================================================
  // HANDLERS
  // ============================================================================
  const handleAdd = () => { setSelectedBill(null); setShowAddModal(true); };
  
  const handleView = (bill, payment = null) => {
    setSelectedBill(bill);
    setViewedFromPayment(payment);
    setShowViewModal(true);
  };

  const handleEdit = (bill, fromViewModal = false) => {
    setSelectedBill(bill);
    setReturnToViewModal(fromViewModal);
    setShowViewModal(false);
    setShowEditModal(true);
  };

  const handleDelete = (bill, fromViewModal = false) => {
    setSelectedBill(bill);
    setReturnToViewModal(fromViewModal);
    setShowViewModal(false);
    setShowDeleteModal(true);
  };

  const handleMarkPaid = (bill, fromViewModal = false) => {
    setSelectedBill({ ...bill, next_due_date: bill.dueInfo?.dueDate || new Date().toISOString().split('T')[0] });
    setReturnToViewModal(fromViewModal);
    setShowViewModal(false);
    setShowMarkPaidModal(true);
  };

  const handleToggleActive = async (bill) => {
    try {
      await billsAPI.toggleActive(bill.id, !bill.is_active);
      success(`Bill ${bill.is_active ? 'deactivated' : 'activated'}`);
      loadData(true);
    } catch (error) {
      showError('Failed to update bill status');
    }
  };

  const confirmDelete = async () => {
    if (!selectedBill) return;
    try {
      await billsAPI.delete(selectedBill.id);
      success('Bill deleted successfully');
      setShowDeleteModal(false);
      setSelectedBill(null);
      loadData(true);
    } catch (error) {
      showError('Failed to delete bill');
    }
  };

  const submitMarkAsPaid = async (paymentData) => {
    setSaving(true);
    try {
      await paymentsAPI.record(paymentData);
      success('Payment recorded successfully');
      setShowMarkPaidModal(false);
      setSelectedBill(null);
      setReturnToViewModal(false);
      loadData(true);
    } catch (error) {
      showError('Failed to record payment');
    } finally {
      setSaving(false);
    }
  };

  const handleEditPayment = (payment) => {
    setSelectedPayment(payment);
    setShowViewModal(false);
    setShowEditPaymentModal(true);
  };

  const submitEditPayment = async (paymentId, paymentData) => {
    setSaving(true);
    try {
      await paymentsAPI.update(paymentId, {
        payment_date: paymentData.paymentDate,
        amount: paymentData.amount,
        payment_account_id: paymentData.paymentAccountId,
        confirmation_number: paymentData.confirmationNumber,
        notes: paymentData.notes
      });
      success('Payment updated successfully');
      setShowEditPaymentModal(false);
      setSelectedPayment(null);
      setSelectedBill(null);
      setViewedFromPayment(null);
      loadData(true);
    } catch (error) {
      showError('Failed to update payment');
    } finally {
      setSaving(false);
    }
  };

  const deletePayment = async (paymentId) => {
    setSaving(true);
    try {
      await paymentsAPI.delete(paymentId);
      success('Payment deleted successfully');
      setShowEditPaymentModal(false);
      setSelectedPayment(null);
      setSelectedBill(null);
      setViewedFromPayment(null);
      loadData(true);
    } catch (error) {
      showError('Failed to delete payment');
    } finally {
      setSaving(false);
    }
  };

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    localStorage.setItem('manageViewMode', mode);
  };

  const toggleSection = (key) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // ============================================================================
  // MONTH NAVIGATION
  // ============================================================================
  const goToPrevMonth = () => {
    setCalendarDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCalendarDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCalendarDate(new Date());
  };

  const monthLabel = calendarDate.toLocaleString('default', { month: 'long', year: 'numeric' });

  // ============================================================================
  // RENDER HELPERS
  // ============================================================================
  const renderBillItem = (bill, showPaidStatus = false) => {
    const cat = getCategoryInfo(bill.category);
    const { isPaid, isOverdue, dueDate } = bill.dueInfo;
    
    return (
      <div 
        key={bill.id}
        className={`bm-bill-item ${isPaid ? 'paid' : ''} ${isOverdue ? 'overdue' : ''}`}
        onClick={() => handleView(bill)}
      >
        {showPaidStatus && isPaid && <CheckCircle size={16} className="paid-check" />}
        <BillIcon iconUrl={bill.icon_url} categoryIcon={cat.icon} billName={bill.bill_name} size="sm" />
        <div className="bm-bill-info">
          <span className="bm-bill-name">
            {bill.bill_name}
            {bill.auto_pay && <Zap size={12} className="autopay-icon" />}
          </span>
          <span className={`bm-bill-due ${isOverdue ? 'overdue' : ''}`}>
            {dueDate ? formatDateRelative(dueDate) : 'No date'}
          </span>
        </div>
        <span className={`bm-bill-amount ${isPaid ? 'paid' : ''}`}>{formatAmount(bill.amount)}</span>
      </div>
    );
  };

  const renderPaymentItem = (payment) => {
    const cat = getCategoryInfo(payment.category);
    return (
      <div 
        key={payment.id}
        className="bm-bill-item paid"
        onClick={() => {
          const bill = bills.find(b => b.id === payment.bill_id);
          if (bill) handleView({ ...bill, dueInfo: getNextDueInfo(bill) }, payment);
        }}
      >
        <BillIcon iconUrl={payment.icon_url} categoryIcon={cat.icon} billName={payment.bill_name} size="sm" />
        <div className="bm-bill-info">
          <span className="bm-bill-name">{payment.bill_name}</span>
          <span className="bm-bill-due">{formatDateRelative(payment.payment_date)}</span>
        </div>
        <span className="bm-bill-amount paid">{formatAmount(payment.amount)}</span>
      </div>
    );
  };

  // ============================================================================
  // LOADING STATE
  // ============================================================================
  if (loading) {
    return (
      <div className="bm-page">
        <div className="loading-state">Loading bills...</div>
      </div>
    );
  }

  // ============================================================================
  // RENDER
  // ============================================================================
  return (
    <div className="bm-page">
      {/* ===== HEADER ===== */}
      <div className="bm-header">
        <h1>Bill Manager</h1>
        <div className="bm-header-buttons">
          <button className="btn btn-income" onClick={() => setShowIncomeModal(true)} title="Manage Income">
            <Wallet size={18} />
            <span className="bm-btn-text">Income</span>
          </button>
          <button className="btn btn-secondary" onClick={() => setShowBulkImportModal(true)} title="Import Bills">
            <Upload size={18} />
            <span className="bm-btn-text">Import</span>
          </button>
          <button className="btn btn-primary" onClick={handleAdd}>
            <PlusCircle size={18} />
            <span className="bm-btn-text">Add Bill</span>
          </button>
        </div>
      </div>

      {/* ===== TOOLBAR ===== */}
      <div className="bm-toolbar">
        <div className="bm-view-toggle">
          <button className={`bm-view-btn ${viewMode === 'bento' ? 'active' : ''}`} onClick={() => handleViewModeChange('bento')} title="Dashboard">
            <LayoutGrid size={18} />
          </button>
          <button className={`bm-view-btn ${viewMode === 'calendar' ? 'active' : ''}`} onClick={() => handleViewModeChange('calendar')} title="Calendar">
            <Calendar size={18} />
          </button>
          <button className={`bm-view-btn ${viewMode === 'agenda' ? 'active' : ''}`} onClick={() => handleViewModeChange('agenda')} title="Agenda">
            <List size={18} />
          </button>
        </div>
        
        <div className="bm-search-filter">
          <BillSearchInput 
            bills={processedBills} 
            categories={categories}
            paymentAccounts={paymentAccounts}
            members={members}
            onBillSelect={handleView}
            placeholder="Search bills..."
            showInactive={showInactive}
            onShowInactiveChange={setShowInactive}
          />
        </div>
      </div>

      {/* ===== MONTH NAV BAR (shared across all views) ===== */}
      <div className="month-nav-bar">
        <div className="month-nav-controls">
          <button className="month-nav-btn" onClick={goToPrevMonth}><ChevronLeft size={20} /></button>
          <button className="month-nav-today" onClick={goToToday}>Today</button>
          <button className="month-nav-btn" onClick={goToNextMonth}><ChevronRight size={20} /></button>
          <span className="month-nav-label">{monthLabel}</span>
        </div>
        <div className="month-nav-stats">
          <div className="month-stat">
            <span className="month-stat-label">Due</span>
            <span className="month-stat-value">{formatAmount(monthlyData.stats.totalDue)}</span>
          </div>
          <div className="month-stat">
            <span className="month-stat-label">Paid</span>
            <span className="month-stat-value paid">{formatAmount(monthlyData.stats.totalPaid)}</span>
          </div>
          <div className="month-stat">
            <span className="month-stat-label">Remaining</span>
            <span className="month-stat-value">{formatAmount(monthlyData.stats.remaining)}</span>
          </div>
          {monthlyData.stats.overdueCount > 0 && (
            <div className="month-stat overdue">
              <span className="month-stat-label">{monthlyData.stats.overdueCount} Overdue</span>
              <span className="month-stat-value">{formatAmount(monthlyData.stats.overdueAmount)}</span>
            </div>
          )}
        </div>
      </div>

      {/* ===== BENTO VIEW ===== */}
      {viewMode === 'bento' && (
        <>
          {/* Overdue Banner */}
          {monthlyData.overdueBills.length > 0 && (
            <div className="overdue-banner">
              <div className="overdue-header">
                <div className="overdue-title">
                  <AlertCircle size={18} />
                  <span>Overdue</span>
                </div>
                <div className="overdue-summary">
                  <span className="overdue-count">{monthlyData.overdueBills.length} bill{monthlyData.overdueBills.length !== 1 ? 's' : ''}</span>
                  <span className="overdue-total">{formatAmount(monthlyData.stats.overdueAmount)}</span>
                </div>
              </div>
              <div className="overdue-list">
                {(expandedSections.overdue ? monthlyData.overdueBills : monthlyData.overdueBills.slice(0, 8)).map(bill => {
                  const cat = getCategoryInfo(bill.category);
                  return (
                    <div key={bill.id} className="overdue-item" onClick={() => handleView(bill)}>
                      <BillIcon iconUrl={bill.icon_url} categoryIcon={cat.icon} billName={bill.bill_name} size="sm" />
                      <div className="overdue-item-info">
                        <span className="overdue-item-name">{bill.bill_name}</span>
                        <span className="overdue-item-due">Was due {formatDateRelative(bill.dueInfo.dueDate)}</span>
                      </div>
                      <span className="overdue-item-amount">{formatAmount(bill.amount)}</span>
                    </div>
                  );
                })}
              </div>
              {monthlyData.overdueBills.length > 8 && (
                <button className="overdue-expand" onClick={() => toggleSection('overdue')}>
                  {expandedSections.overdue ? <><ChevronUp size={14} /> Show less</> : <><ChevronDown size={14} /> Show {monthlyData.overdueBills.length - 8} more</>}
                </button>
              )}
            </div>
          )}

          {/* Bento Grid */}
          <div className="bm-bento-grid">
            {/* This Month's Bills */}
            <div className="bm-card bm-card-wide">
              <div className="bm-card-header">
                <div className="bm-card-title">
                  <Calendar size={20} />
                  <span>{calendarDate.toLocaleString('default', { month: 'long' })} Bills</span>
                </div>
                <span className="bm-card-count">{monthlyData.monthBills.length} bills</span>
              </div>
              <div className="bm-card-content bm-bills-grid">
                {monthlyData.monthBills.length === 0 ? (
                  <div className="bm-empty">
                    <Calendar size={24} />
                    <span>No bills this month</span>
                  </div>
                ) : (
                  (expandedSections.monthBills ? monthlyData.monthBills : monthlyData.monthBills.slice(0, 6)).map(bill => renderBillItem(bill, true))
                )}
                {monthlyData.monthBills.length > 6 && (
                  <button className="bm-card-expand bm-month-bills-expand" onClick={() => toggleSection('monthBills')}>
                    {expandedSections.monthBills ? <><ChevronUp size={14} /> Show less</> : <><ChevronDown size={14} /> Show {monthlyData.monthBills.length - 6} more</>}
                  </button>
                )}
              </div>
            </div>

            {/* Bottom Cards Row */}
            <div className={`bm-bottom-cards bento-count-${Math.min((incomeCalcs.hasIncome ? 1 : 0) + (monthlyData.noDueDateBills.length > 0 ? 1 : 0) + (recentPaymentsFiltered.length > 0 ? 1 : 0) + (monthlyData.nextMonth.count > 0 ? 1 : 0), 3)}`}>

            {/* Monthly Income Card */}
            {incomeCalcs.hasIncome && (() => {
              const monthStart = new Date(calendarDate.getFullYear(), calendarDate.getMonth(), 1);
              const monthEnd = new Date(calendarDate.getFullYear(), calendarDate.getMonth() + 1, 0, 23, 59, 59);
              const paydays = incomeCalcs.getPaydaysInRange(monthStart, monthEnd);
              return (
                <BentoCard
                  icon={<DollarSign size={20} />}
                  title="Monthly Income"
                  headerVariant="income"
                  info={
                    <>
                      <div className="bento-info-primary">{formatAmount(incomeCalcs.totalMonthlyIncome)}<span>/month</span></div>
                      <div className="bento-info-stats">
                        <div className="bento-info-stat">
                          <span className="bento-info-stat-label">Bills</span>
                          <span className="bento-info-stat-value">{formatAmount(incomeCalcs.totalMonthlyBills)}</span>
                        </div>
                        <div className="bento-info-stat">
                          <span className="bento-info-stat-label">Left</span>
                          <span className="bento-info-stat-value positive">{formatAmount(incomeCalcs.totalMonthlyIncome - incomeCalcs.totalMonthlyBills)}</span>
                        </div>
                      </div>
                    </>
                  }
                  items={paydays}
                  renderItem={(p, i) => (
                    <div key={`${p.id}-${p.date}-${i}`} className="bm-bill-item" onClick={() => {
                      const income = incomeData.personal.find(inc => inc.id === p.id);
                      if (income) { setSelectedIncome(income); setShowViewIncomeModal(true); }
                    }}>
                      <BillIcon iconUrl={p.icon_url} categoryIcon="💵" billName={p.income_name} size="sm" />
                      <div className="bm-bill-info">
                        <span className="bm-bill-name">{p.income_name}</span>
                        <span className="bm-bill-due">{formatDateRelative(p.date)}</span>
                      </div>
                      <span className="bm-bill-amount positive">+{formatAmount(p.amount)}</span>
                    </div>
                  )}
                  collapseAt={5}
                  expanded={expandedSections.paydays}
                  onToggleExpand={() => toggleSection('paydays')}
                />
              );
            })()}

            {/* No Due Date */}
            {monthlyData.noDueDateBills.length > 0 && (
              <BentoCard
                icon={<Calendar size={20} />}
                title="No Due Date"
                headerVariant="muted"
                headerRight={<span className="bm-card-count">{monthlyData.noDueDateBills.length}</span>}
                items={monthlyData.noDueDateBills}
                renderItem={(bill) => renderBillItem(bill)}
                collapseAt={5}
                expanded={expandedSections.noDate}
                onToggleExpand={() => toggleSection('noDate')}
              />
            )}

            {/* Recent Payments */}
            {recentPaymentsFiltered.length > 0 && (
              <BentoCard
                icon={<CreditCard size={20} />}
                title="Recent Payments"
                headerVariant="success"
                info={
                  <>
                    <div className="bento-info-primary">{formatAmount(recentPaymentsFiltered.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0))}</div>
                    <div className="bento-info-secondary">{recentPaymentsFiltered.length} payments in last 14 days</div>
                  </>
                }
                items={recentPaymentsFiltered}
                renderItem={(payment) => renderPaymentItem(payment)}
                collapseAt={5}
                expanded={expandedSections.payments}
                onToggleExpand={() => toggleSection('payments')}
              />
            )}

            {/* Next Month Preview */}
            {monthlyData.nextMonth.count > 0 && (
              <BentoCard
                icon={<Calendar size={20} />}
                title={`${new Date(calendarDate.getFullYear(), calendarDate.getMonth() + 1).toLocaleString('default', { month: 'long' })} Preview`}
                info={
                  <>
                    <div className="bento-info-primary">{formatAmount(monthlyData.nextMonth.total)}</div>
                    <div className="bento-info-secondary">{monthlyData.nextMonth.count} bills next month</div>
                  </>
                }
                items={monthlyData.nextMonth.preview}
                renderItem={(bill) => {
                  const cat = getCategoryInfo(bill.category);
                  return (
                    <div key={bill.id} className="bm-bill-item" onClick={() => handleView(bill)}>
                      <BillIcon iconUrl={bill.icon_url} categoryIcon={cat.icon} billName={bill.bill_name} size="sm" />
                      <div className="bm-bill-info">
                        <span className="bm-bill-name">
                          {bill.bill_name}
                          {bill.auto_pay && <Zap size={12} className="autopay-icon" />}
                        </span>
                      </div>
                      <span className="bm-bill-amount">{formatAmount(bill.amount)}</span>
                    </div>
                  );
                }}
                collapseAt={5}
                expanded={expandedSections.preview}
                onToggleExpand={() => toggleSection('preview')}
              />
            )}

            </div>{/* End Bottom Cards Row */}

            {/* Inactive Bills */}
            {showInactive && monthlyData.inactiveBills.length > 0 && (
              <div className="bm-card">
                <div className="bm-card-header muted">
                  <div className="bm-card-title">
                    <EyeOff size={20} />
                    <span>Inactive</span>
                  </div>
                  <span className="bm-card-count">{monthlyData.inactiveBills.length}</span>
                </div>
                <div className="bm-card-content">
                  {(expandedSections.inactive ? monthlyData.inactiveBills : monthlyData.inactiveBills.slice(0, 5)).map(bill => renderBillItem(bill))}
                  {monthlyData.inactiveBills.length > 5 && (
                    <button className="bm-card-expand" onClick={() => toggleSection('inactive')}>
                      {expandedSections.inactive ? <><ChevronUp size={14} /> Show less</> : <><ChevronDown size={14} /> Show {monthlyData.inactiveBills.length - 5} more</>}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* ===== CALENDAR VIEW ===== */}
      {viewMode === 'calendar' && (
        <CalendarView
          bills={showInactive ? bills : bills.filter(b => b.is_active)}
          payments={recentPayments}
          categories={categories}
          onBillClick={(bill, date, payment) => handleView(bill, payment)}
          onAddClick={handleAdd}
          onPaydayClick={(payday) => {
            const income = incomeData.personal.find(i => i.id === payday.id);
            if (income) { setSelectedIncome(income); setShowViewIncomeModal(true); }
          }}
          currentDate={calendarDate}
          onDateChange={setCalendarDate}
          incomeCalcs={incomeCalcs}
        />
      )}

      {/* ===== AGENDA VIEW ===== */}
      {viewMode === 'agenda' && (
        <AgendaView
          bills={showInactive ? bills : bills.filter(b => b.is_active)}
          payments={recentPayments}
          categories={categories}
          onBillClick={(bill, date, payment) => handleView(bill, payment)}
          onAddClick={handleAdd}
          onPaydayClick={(payday) => {
            const income = incomeData.personal.find(i => i.id === payday.id);
            if (income) { setSelectedIncome(income); setShowViewIncomeModal(true); }
          }}
          currentDate={calendarDate}
          onDateChange={setCalendarDate}
          incomeCalcs={incomeCalcs}
          allExpanded={false}
        />
      )}

      {/* ===== MODALS ===== */}
      {showAddModal && (
        <BillWizard
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSubmit={async (data) => {
            try {
              await billsAPI.create(data);
              success('Bill created successfully');
              setShowAddModal(false);
              loadData(true);
            } catch (error) { throw error; }
          }}
          paymentAccounts={paymentAccounts}
          members={members}
          categories={categories}
        />
      )}

      {showViewModal && selectedBill && (
        <ViewBillModal
          isOpen={showViewModal}
          onClose={() => { setShowViewModal(false); setSelectedBill(null); setViewedFromPayment(null); }}
          bill={selectedBill}
          onEdit={handleEdit}
          onMarkPaid={handleMarkPaid}
          onToggleActive={handleToggleActive}
          onDelete={handleDelete}
          paymentAccounts={paymentAccounts}
          members={members}
          isInHousehold={isInHousehold}
          payment={viewedFromPayment}
          onEditPayment={handleEditPayment}
        />
      )}

      {showEditModal && selectedBill && (
        <BillWizard
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            if (returnToViewModal) { setShowViewModal(true); setReturnToViewModal(false); }
            else setSelectedBill(null);
          }}
          bill={selectedBill}
          onSubmit={async (data) => {
            try {
              await billsAPI.update(selectedBill.id, data);
              success('Bill updated successfully');
              setShowEditModal(false);
              setSelectedBill(null);
              setReturnToViewModal(false);
              loadData(true);
            } catch (error) { throw error; }
          }}
          paymentAccounts={paymentAccounts}
          members={members}
          categories={categories}
        />
      )}

      {showDeleteModal && selectedBill && (
        <ConfirmModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            if (returnToViewModal) { setShowViewModal(true); setReturnToViewModal(false); }
            else setSelectedBill(null);
          }}
          onConfirm={confirmDelete}
          title="Delete Bill"
          message={`Are you sure you want to delete "${selectedBill.bill_name}"? This action cannot be undone.`}
          confirmText="Delete Bill"
          cancelText="Cancel"
          variant="danger"
        />
      )}

      <MarkAsPaidModal
        isOpen={showMarkPaidModal}
        onClose={() => {
          setShowMarkPaidModal(false);
          if (returnToViewModal) { setShowViewModal(true); setReturnToViewModal(false); }
          else setSelectedBill(null);
        }}
        bill={selectedBill}
        paymentAccounts={paymentAccounts}
        onSubmit={submitMarkAsPaid}
        saving={saving}
      />

      <EditPaymentModal
        isOpen={showEditPaymentModal}
        onClose={() => {
          setShowEditPaymentModal(false);
          setSelectedPayment(null);
          setSelectedBill(null);
          setViewedFromPayment(null);
        }}
        payment={selectedPayment}
        bill={selectedBill}
        paymentAccounts={paymentAccounts}
        onSubmit={submitEditPayment}
        onDelete={deletePayment}
        saving={saving}
      />

      <BulkImportModal
        isOpen={showBulkImportModal}
        onClose={() => { setShowBulkImportModal(false); loadData(true); }}
        onImport={async (billData) => { await billsAPI.create(billData); }}
        paymentAccounts={paymentAccounts}
        members={members}
        categories={categories}
        isInHousehold={isInHousehold}
      />

      <ManageIncomeModal
        isOpen={showIncomeModal}
        onClose={() => {
          setShowIncomeModal(false);
          if (returnToViewIncomeModal && selectedIncome) {
            setShowViewIncomeModal(true);
            setReturnToViewIncomeModal(false);
          } else {
            setSelectedIncomeId(null);
            setSelectedIncome(null);
          }
        }}
        onDataChange={() => {
          loadData(true);
          if (returnToViewIncomeModal && selectedIncomeId) {
            incomeAPI.getAll().then(res => {
              const updated = res.data?.find(i => i.id === selectedIncomeId);
              if (updated) setSelectedIncome(updated);
            });
          }
        }}
        editIncomeId={selectedIncomeId}
      />

      <ViewIncomeModal
        isOpen={showViewIncomeModal}
        onClose={() => { setShowViewIncomeModal(false); setSelectedIncome(null); }}
        income={selectedIncome}
        onEdit={(income) => {
          setShowViewIncomeModal(false);
          setSelectedIncomeId(income.id);
          setReturnToViewIncomeModal(true);
          setShowIncomeModal(true);
        }}
        onDelete={async (income) => {
          try {
            await incomeAPI.delete(income.id);
            success('Income deleted successfully');
            setShowViewIncomeModal(false);
            setSelectedIncome(null);
            loadData(true);
          } catch (error) {
            showError('Failed to delete income');
          }
        }}
        currentUserId={currentUser?.id}
      />
    </div>
  );
}

export default BillManager;
